/**
 * Turn a failed revert into something the person reading it can act on.
 *
 * The server returns two fields on a 500: a fixed `error` ("Failed to revert action") and a
 * `details` carrying the actual database message. The UI only ever showed `error`, so a
 * genuine schema bug — a column that did not exist — reached the user as five words with no
 * cause, no next step, and nothing to report. It stayed that way because the one string the
 * screen could show was the same string for every possible failure.
 *
 * Deliberate errors (chain-lock, the 7-day window, the month-end ledger lock) already carry
 * a complete sentence in `error`, and those must read exactly as written — appending a
 * technical detail to "revert the newer changes first" only muddies an instruction that is
 * already clear. So `details` is appended only when `error` is the generic fallback.
 *
 * TWO ERROR SHAPES, because there are two transports. Axios nests the body under
 * `err.response.data`; services/httpClient.js is a fetch wrapper that attaches it flat as
 * `err.data`. Reading only the axios shape is why this function quietly stopped working on
 * the screens that use httpClient: `details` was invisible, so a 500 fell through to
 * `err.message` — which httpClient sets to `data.error`, i.e. the generic five words this
 * whole function exists to replace. It can also be handed a resolved `{success:false}` body
 * directly (httpClient returns rather than throws on 403), so a plain body is accepted too.
 */
/**
 * The fixed `error` strings a 500 carries — the ones that say nothing about what went
 * wrong. Only for these is `details` worth appending. The stock-in revert route
 * (backend/routes/stockInV2.js) words its own generic failure differently, and it was not
 * listed here, so a genuine database fault on a stock-in revert still reached the user as
 * "Failed to revert stock in record" with the cause discarded.
 */
const GENERIC_SERVER_ERROR = /^failed to revert (action|stock[ -]?in(?: record)?)\.?$/i;

/** The response body, wherever this particular transport decided to put it. */
function errorBody(err) {
  if (!err || typeof err !== 'object') return {};
  if (err.response && err.response.data && typeof err.response.data === 'object') {
    return err.response.data;
  }
  if (err.data && typeof err.data === 'object') return err.data;
  // A body passed straight in: recognised by its own fields, never by the absence of others.
  if (typeof err.error === 'string' || typeof err.details === 'string') return err;
  return {};
}

export function revertErrorMessage(err) {
  const data = errorBody(err);
  const serverError = typeof data.error === 'string' ? data.error.trim() : '';
  const details = typeof data.details === 'string' ? data.details.trim() : '';

  if (serverError && !GENERIC_SERVER_ERROR.test(serverError)) {
    return serverError;
  }

  if (details) {
    return `The revert could not be completed: ${details}. Nothing was changed — the whole revert is rolled back on failure. Please report this message.`;
  }

  if (serverError) return serverError;
  if (err && err.message) return err.message;
  return 'Failed to revert action.';
}

export default revertErrorMessage;
